import { RouteName } from "constant/routeNames";
import { RiPlayList2Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { FaCrown } from "react-icons/fa";
import { ContentType } from "services/subscription/types";
import { usePremiumAccess } from "hooks/usePremiumAccess";
import PremiumContentModal from "components/PremiumContentModal";

interface PackageResponse {
  id: number;
  entitlement_id: string;
  title: string;
  price: string;
  thumbnail: string;
  created_at: string;
  description: string;
  is_subscribed: boolean;
  role_id: number;
  color?: string;
  slug: string;
}

function PlaylistCard({ playlist, creatorData }: any) {
  const navigate = useNavigate();
  const { isModalOpen, setIsModalOpen, checkAccess } = usePremiumAccess();

  const streamKey = playlist?.first_video_stream_key;
  const isPremiumContent = playlist?.plan_id === 2;

  const packageObject: PackageResponse = {
    id: creatorData?.general_info?.service_id,
    entitlement_id: creatorData?.general_info?.entitlement_id,
    title: creatorData?.general_info?.name,
    price: creatorData?.general_info?.price,
    thumbnail: creatorData?.general_info?.thumbnail_url,
    created_at: creatorData?.general_info?.created_at || "",
    description: creatorData?.general_info?.description,
    is_subscribed: creatorData?.general_info?.is_Subscribed,
    role_id: creatorData?.general_info?.plan_id,
    slug: creatorData?.general_info?.slug,
  };

  const handlePlaylist = async () => {
    const access = await checkAccess({
      contentType: ContentType.CREATOR_PLAYLISTS,
      id: playlist?.id,
      isPremiumContent,
      isCreator: true,
    });
    if (!access) return;

    if (streamKey) {
      navigate(
        RouteName.CreatorPlaylistRoute.replace(
          ":slug",
          creatorData?.general_info?.slug,
        ).replace(":stream", streamKey),
      );
    }
  };

  if (!streamKey) return null;

  return (
    <>
      <PremiumContentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isCreator={true}
        packageObject={packageObject}
      />

      <div onClick={handlePlaylist}>
        <div className="group block w-[98%] cursor-pointer rounded-xl px-2 py-2 shadow-lg transition-all duration-300 ease-out hover:-translate-y-1 hover:bg-slate-800 hover:shadow-2xl hover:shadow-[var(--red)]/10 max-sm:my-0">
          <div className="relative aspect-video w-full overflow-hidden rounded-xl ring-1 ring-white/5 transition-all duration-300 group-hover:ring-2 group-hover:ring-[var(--red)]/50 max-sm:w-full">
            {isPremiumContent && (
              <div className="absolute left-2.5 top-2.5 z-20 flex items-center gap-1 rounded-full border border-white/15 bg-black/40 px-2 py-1 text-[10px] font-semibold text-white backdrop-blur-md">
                <FaCrown className="text-[10px] text-[var(--red)]" />
                <span>PREMIUM</span>
              </div>
            )}

            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-out group-hover:scale-110"
              style={{
                backgroundImage: `url(${playlist?.first_video_thumbnail_url})`,
              }}
            />

            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-3">
              <div
                data-tooltip-id={
                  playlist?.title?.length > 40
                    ? `playlist-title-${playlist?.id || playlist?.slug}`
                    : undefined
                }
              >
                <p className="line-clamp-2 text-[14px] font-[700] leading-tight text-white">
                  {playlist?.title}
                </p>

                {playlist?.title?.length > 40 && (
                  <ReactTooltip
                    place="top"
                    id={`playlist-title-${playlist?.id || playlist?.slug}`}
                    content={playlist?.title}
                    style={{ width: "180px" }}
                  />
                )}
              </div>

              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--red)] text-white shadow-lg transition-transform duration-300 group-hover:scale-110"
              >
                <RiPlayList2Line className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PlaylistCard;
